import { phoneNumbers } from 'e2e/fixtures/phoneNumbers'

import { firstNames, lastNames } from '../fixtures/names'
import { TITLES } from 'directFlow/pages/Drivers/config'
import { generateDrivingLicenceNumberPrefill } from 'directFlow/pages/Drivers/utils/utils'

import { CompleteDateObject } from 'src/types/date'

const getRandomValueFromArray = <T>(array: T[]): T => {
  const randomIndex = Math.floor(Math.random() * array.length)
  return array[randomIndex]
}

export const getRandomFirstName = () => {
  return getRandomValueFromArray(firstNames)
}

export const getRandomLastName = () => {
  return getRandomValueFromArray(lastNames)
}

const getRandomPhoneNumber = () => {
  return getRandomValueFromArray(phoneNumbers)
}

const removeWhitespaceAndSpecialCharacters = (str: string) => str.replace(/[\s\-']/g, '')

const processOptions = ({
  passfraud,
  forceOnfido,
}: {
  passfraud: boolean
  forceOnfido: boolean
}) => {
  if (!passfraud) {
    return '+e2etest'
  }
  return `+passfraud${forceOnfido ? '-force_on' : ''}-e2etest`
}

export const getRandomEmail = (
  { firstName, lastName }: { firstName: string; lastName: string },
  emailOptions: { passfraud: boolean; forceOnfido: boolean } = {
    passfraud: true,
    forceOnfido: true,
  },
) => {
  const noWhiteSpaceFirstName = removeWhitespaceAndSpecialCharacters(firstName)
  const nowhitespaceSurname = removeWhitespaceAndSpecialCharacters(lastName)
  const EMAIL_PREFIX = 'e2e'
  const EMAIL_SUFFIX = '@marshmallow.co'

  // `force_on` is used to force onfido checks it only works with `+passfraud` flag
  // for more information check the following link
  // https://www.notion.so/getmarshmallow/Bypassing-fraud-checks-in-Staging-1c1c6747c7338061b3e4c8db1b0b6447?pvs=4
  const flags = processOptions(emailOptions)
  const randomNumber = Math.floor(Math.random() * 1001)

  // In case we need to add more flags we may just generate a random number instead of using names
  const randomPart =
    `-${randomNumber}.${noWhiteSpaceFirstName.toLowerCase()}${nowhitespaceSurname.toLowerCase()}`.slice(
      0,
      20,
    )

  // ! Part of the email before the @ can have up to 64 characters
  return `${EMAIL_PREFIX}${flags}${randomPart}${EMAIL_SUFFIX}`
}

const getRandomTitle = () => {
  return getRandomValueFromArray(TITLES)
}

// Used for generating a driving licence number in tests for user details with geneder neutral titles
export const getTitleByRandomSexAssignedAtBirth = (title: string) => {
  const genderNeutralTitles = ['Mx', 'Dr']

  if (genderNeutralTitles.includes(title)) {
    return Math.random() > 0.5 ? 'Mr' : 'Mrs'
  }

  return title
}

/**
 Generates a random first name
 A middle name will be generated randomly 50% of the time
 */
const generateFirstName = () => {
  const firstName = getRandomFirstName()
  const middleName = getRandomFirstName()

  return Math.random() > 0.5 ? `${firstName} ${middleName}` : firstName
}

/**
 Generates a random surname
 A second surname will be generated randomly 50% of the time
 */
const generateSurname = () => {
  const randomSurname = getRandomLastName()
  const randomSurname2 = getRandomLastName()
  const separator = Math.random() > 0.5 ? '-' : ' '

  return Math.random() > 0.5 ? `${randomSurname}${separator}${randomSurname2}` : randomSurname
}

export const getRandomUserDetails = (options?: {
  emailOptions?: { passfraud: boolean; forceOnfido: boolean }
  customDateOfBirth?: CompleteDateObject
}) => {
  const title = getRandomTitle()
  const firstName = generateFirstName()
  const lastName = generateSurname()
  const phone = getRandomPhoneNumber()
  const email = getRandomEmail({ firstName, lastName }, options?.emailOptions)
  const dateOfBirth = options?.customDateOfBirth ?? {
    day: '01',
    month: '01',
    year: '1990',
  }

  return {
    title,
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    first11CharactersOfDrivingLicenceNumber: generateDrivingLicenceNumberPrefill({
      title: getTitleByRandomSexAssignedAtBirth(title),
      lastName,
      dateOfBirth,
    }),
  }
}
